<script setup>
import { lohnletterRetrieve } from '@/client'
import LohnletterListComponent from '@/components/LohnletterListComponent.vue'
import { useTextStore, toUnderscore, toDash } from '@/stores/texts'
import { inject } from 'vue'
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { client } from '@/lohnrechner'

import format from 'string-format'

const props = defineProps({
  slug: {
    type: String,
    required: true
  }
})

const letter = ref(null)

const languageStore = useTextStore()

const router = useRouter()
const setTitle = inject('setTitle')

watch(
  () => props.slug,
  async () => {
    fetchPage()
  }
)

languageStore.$subscribe((mutation, state) => {
  if (!letter.value) return
  let lang = toDash(letter.value.language)
  if (lang === state.language) return
  for (let translation of letter.value.translations) {
    if (toDash(translation.language) !== languageStore.language) continue

    let slug = translation.slug
    router.push({ name: 'lohnletter-detail', params: { slug: slug } })
    return
  }
  router.push({ name: 'lohnletter' })
})

function fetchPage() {
  lohnletterRetrieve({
    client,
    path: { slug: props.slug },
    query: { language: toUnderscore(languageStore.language) }
  }).then((response) => {
    letter.value = response.data
    setTitle(letter.value.title)
  })
}
fetchPage()
function formatDate(date) {
  return new Date(date).toLocaleDateString(languageStore.language, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  })
}

const t = inject('t')
</script>
<template>
  <article v-if="letter">
    <span class="date">{{ format(t('published-at'), formatDate(letter.created_at)) }}</span>
    <h2>{{ letter.title }}</h2>
    <p class="lead">{{ letter.lead }}</p>

    <section class="content" v-html="letter.content"></section>
  </article>

  <section class="teaser">
    <span>{{ t('teaser-lohnletter') }}</span>
    <RouterLink :to="{ name: 'home' }" class="button">
      <span>
        {{ t('teaser-lohnletter-button') }}
      </span>
      <svg
        data-v-72a9a2a9=""
        width="54"
        height="54"
        viewBox="0 0 54 54"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          data-v-72a9a2a9=""
          d="M37.958 26.395L26.9893 37.364L16.0204 26.395"
          stroke="#e2001a"
          stroke-width="3.04252"
        ></path>
        <line
          data-v-72a9a2a9=""
          x1="26.9817"
          y1="35.7695"
          x2="26.9817"
          y2="16.7395"
          stroke="#e2001a"
          stroke-width="3.04252"
        ></line>
      </svg>
    </RouterLink>
  </section>

  <section class="more">
    <h2 class="more">{{ t('tipps-und-tricks') }}</h2>
    <LohnletterListComponent></LohnletterListComponent>
  </section>
</template>
<style>
.content h1 {
  font-size: calc(var(--font-size) * 1.5);
  color: var(--color-text);
  text-align: left;
  margin-bottom: 6px;
  letter-spacing: 0;

  font-family: Theinhardt;
  font-weight: bold;
  font-size: 1.3rem;
}

figcaption {
  font-size: 0.8rem;
  text-decoration: none;
  padding: 0 21.6px;
}

figure a {
  text-decoration: none;
  color: var(--color-text);
}

p,
.content {
  letter-spacing: 0.5px;
  line-height: 1.5;
}

.content figure img {
  max-width: 100%;
  border-radius: 21.6px;
}

figure {
  margin-top: 3rem;
}
</style>
<style scoped>
article {
  margin-top: 100px;
  margin-bottom: 120px;
}

h2.more {
  text-align: center;
}

h2 {
  font-size: 2.9rem;
  line-height: 99%;
  text-align: left;
  margin-bottom: 0.5em;
  margin-top: 0;
  letter-spacing: 3px;
  color: var(--color-signal);
}

.lead {
  color: var(--color-signal);
  margin-bottom: 1em;
  text-align: left;
}

span.date {
  display: block;
  font-size: calc(var(--font-size) * 0.9);
  font-weight: 300;
  margin-bottom: 10px;
}

.teaser {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 3rem;
  margin-bottom: 5rem;
  border-radius: 21.6px;
  background-color: var(--color-grey);
  padding: 40px 12px;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
  gap: 30px;
}

.teaser .button {
  display: flex;
  flex-direction: row;
  align-items: center;
  text-decoration: none;
  justify-content: space-between;
  background: var(--color-signal);
  border-radius: 67px;
  color: white;
  font-weight: bold;
  padding: 0px 17px;
  border-width: 4px;
  border: solid 10px var(--color-signal);
  height: 60px;
  transition: all 200ms;
  max-width: 100%;
  width: 520px;
  box-sizing: border-box;
}

.teaser svg {
  background: var(--color-grey);
  transform: rotateZ(-90deg);
  border-radius: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  margin-right: -17px;
  height: 44px;
  width: 44px;
}
</style>
